import React, { useEffect, useState } from 'react'
import entete from "../../../assets/img/entete_pool.png"
import PropTypes from 'prop-types';
import { DocumentFooter } from '../../../components/docs/DocumentFooter';
import { getCompagnieLogo } from '../../../hooks/useFilesHooks';

const frDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

/**
 * DEMANDE D'AVIS SUR DEVIS — courrier adressé au cabinet d'expertise pour solliciter
 * son avis d'expert sur le(s) devis de réparation d'un véhicule endommagé.
 */
const DemandeAvisSurDevis = React.forwardRef((props, ref) => {
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    // En-tête + pied de page OPTIONNELS : désactivés → impression « nue » sur papier à en-tête.
    const afficherEnteteEtPied = props?.afficherEnteteEtPied !== false;

    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((error) => { console.log(error); });
        }
    }, []);

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    const sinistre = props?.sinistre;
    const expertise = props?.expertise;
    const expert = expertise?.expert;
    const nomExpert = `${expert?.nom || ''} ${expert?.prenom || ''}`.trim();
    const numSinistre = sinistre?.numSinistre || sinistre?.numeroSinistreM || '';
    const dateSinistre = frDate(sinistre?.dateSinistre);
    // Immatriculation à faire figurer : celle du VÉHICULE ADVERSE soumis à l'expertise
    // (engin de la victime/partie adverse de l'expertise), SAUF si la victime de l'expertise
    // est le CONDUCTEUR du véhicule ASSURÉ du sinistre → dans ce cas, le véhicule assuré.
    const beneficiaire = expertise?.beneficiaire;
    const estConducteurAssure = !!beneficiaire
        && beneficiaire.adversaire !== true
        && beneficiaire.conducteur === true;
    const immatAssure = sinistre?.engin?.immatriculation
        || sinistre?.contrat?.engins?.[0]?.immatriculation
        || '';
    const immatAdverse = beneficiaire?.engin?.immatriculation || '';
    const immatriculation = (estConducteurAssure ? immatAssure : (immatAdverse || immatAssure)) || '……………';

    // Cible de la demande d'avis : un « bien » endommagé (téléphone, ordinateur…) ou le
    // véhicule. Un seul des deux s'affiche (cf. remarques sur le modèle) :
    //  - BIEN     → « …réparation du <désignation (marque)> » (pas d'immatriculation) ;
    //  - VEHICULE → « …réparation du véhicule immatriculé <immat> » (pas d'objet).
    const estBien = (expertise?.typeCible || 'VEHICULE').toUpperCase() === 'BIEN';
    const bienLibelle = [expertise?.bienDesignation, expertise?.bienMarque]
        .map((v) => (v || '').trim())
        .filter(Boolean)
        .join(' – ') || '……………';

    return (
        <div ref={ref} style={{
            fontFamily: "Calibri, 'Calibri Light', sans-serif",
            maxWidth: "800px",
            margin: "0 auto",
            padding: "20px 70px",
            lineHeight: 1.6,
            fontSize: "12pt",
            color: "#000000",
            display: "flex",
            flexDirection: "column",
            minHeight: "250mm",
            boxSizing: "border-box"
        }} className="demande-avis">
            {/* Neutralise le « * { font-weight: normal !important } » global (toPrint.css), scopé ici. */}
            <style>
                {`
                    .demande-avis strong,
                    .demande-avis b,
                    .demande-avis .dav-titre { font-weight: bold !important; }
                    @media print {
                        .demande-avis { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                    }
                `}
            </style>

            {/* En-tête avec logos — masquable pour impression sur papier à en-tête. */}
            {afficherEnteteEtPied && (
                <div style={{ marginBottom: "30px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                        {sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && logoPreviewUrl && (
                            <img src={logoPreviewUrl} style={{ width: "auto", height: "60px", objectFit: "contain" }} alt="Logo compagnie" />
                        )}
                        <img src={entete} style={{ width: "auto", height: "60px", objectFit: "contain" }} alt="En-tête" />
                    </div>
                    <div style={{ height: "2px", background: "linear-gradient(to right, #2563eb, #3b82f6)", marginTop: "8px" }} />
                </div>
            )}

            {/* Papier à en-tête : centre le corps verticalement (l'en-tête pré-imprimé occupe le haut). */}
            {!afficherEnteteEtPied && <div style={{ flex: 1 }} />}

            {/* Titre */}
            <div style={{ textAlign: "center", marginBottom: "45px", marginTop: "30px" }}>
                <p className="dav-titre" style={{ margin: 0, fontSize: "14pt", fontWeight: "bold", textDecoration: "underline" }}>
                    DEMANDE D'AVIS SUR DEVIS
                </p>
                <p className="dav-titre" style={{ margin: "10px 0 0 0", fontSize: "12pt", fontWeight: "bold" }}>
                    SINISTRE N° {numSinistre}{dateSinistre ? ` DU ${dateSinistre}` : ''}
                </p>
            </div>

            {/* Corps */}
            <div style={{ textAlign: "justify", fontSize: "12pt", lineHeight: 1.9 }}>
                <p style={{ marginBottom: "20px" }}>
                    Au cabinet d'expertise : <strong>{nomExpert || '……………'}</strong>
                </p>
                <p style={{ marginBottom: "20px" }}>
                    <strong>Messieurs,</strong>
                </p>
                <p style={{ marginBottom: "24px" }}>
                    Nous venons par la présente vous prier de bien vouloir nous donner votre avis
                    d'expert sur le/les devis de réparation{' '}
                    {estBien ? (
                        <>de/du <strong>{bienLibelle}</strong></>
                    ) : (
                        <>du véhicule immatriculé <strong>{immatriculation}</strong></>
                    )}{' '}
                    endommagé au cours de l'accident cité en titre.
                </p>
                <p style={{ marginBottom: "20px" }}>
                    Nous vous en souhaitons et restons dans l'attente de votre retour,
                </p>
                <p style={{ marginBottom: "0" }}>
                    Recevez <strong>Messieurs</strong>, nos salutations distinguées.
                </p>
            </div>

            {/* Signature — « Pour la société » en gras et aligné au DÉBUT de la date (à gauche). */}
            <div style={{ marginTop: "90px", display: "flex", justifyContent: "flex-end" }}>
                <div style={{ textAlign: "left" }}>
                    <p style={{ margin: 0, fontSize: "12pt", textDecoration: "underline" }}>
                        Fait à Lomé, le {today}
                    </p>
                    <p style={{ margin: "4px 0 0 0", fontSize: "12pt" }}>
                        <strong>Pour la société</strong>
                    </p>
                </div>
            </div>

            {/* Espace flexible : pousse le pied de page en bas de la feuille A4. */}
            <div style={{ flex: 1 }} />

            {afficherEnteteEtPied && <DocumentFooter />}
        </div>
    )
})

DemandeAvisSurDevis.propTypes = {
    expertise: PropTypes.object,
    sinistre: PropTypes.object,
    /** false → imprimé sans en-tête ni pied (papier à en-tête de l'utilisateur). */
    afficherEnteteEtPied: PropTypes.bool,
}

export default DemandeAvisSurDevis
